import { ColumnDef } from "@tanstack/react-table";
import { Checkbox, Chip } from "@heroui/react";
import { Link } from "@tanstack/react-router";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import { BaseField } from "@/types/field";
import { getEnrollmentTierLabel } from "@/lib/utils/enrollment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import InsightsActivityChip from "@/components/common/InsightsActivityChip";

export interface FieldColumnOptions {
    growersById: Record<number, Organization>;
    serviceProvidersById: Record<number, Organization>;
    accountManagersById: Record<number, User>;
    userIsSentinel: boolean;
}

export const createFieldColumns = {
    selection: <T extends BaseField>(): ColumnDef<T> => ({
        id: "selection",
        header: ({ table }) => {
            return (
                <Checkbox
                    className="pl-4"
                    isSelected={table.getIsAllRowsSelected()}
                    onValueChange={(value) => {
                        table.toggleAllRowsSelected(value);
                    }}
                />
            );
        },
        enableSorting: false,
        accessorFn: (row) => row?.id ?? "",
        cell: ({ row }) => {
            return (
                <Checkbox
                    className="pl-4"
                    isSelected={row.getIsSelected()}
                    onValueChange={(value) => row.toggleSelected(value)}
                />
            );
        },
        size: 50,
    }),

    growerAndServiceProvider: <T extends BaseField>(
        options: FieldColumnOptions,
    ): ColumnDef<T> => ({
        id: "grower",
        header: options.userIsSentinel ? "Farm/CSP" : "Farm",
        accessorFn: (row) => row?.grower,
        cell: ({ row }) => {
            const { growersById, serviceProvidersById, accountManagersById } =
                options;
            return (
                <div className="flex flex-col">
                    <div>{growersById[row.original.grower]?.name}</div>
                    {options.userIsSentinel && (
                        <div className="text-xs text-gray-500">
                            {serviceProvidersById[row.original.service_provider]
                                ?.name ?? "N/A"}
                            {" - "}
                            {accountManagersById[row.original.account_manager]
                                ?.full_name ?? "N/A"}
                        </div>
                    )}
                </div>
            );
        },
        size: 250,
    }),

    fieldName: <
        T extends BaseField & {
            crop_zone_name?: string | null;
            crop_zone_id?: number | null;
            crop_zone_color?: string | null;
        },
    >(): ColumnDef<T> => ({
        id: "name",
        header: "Field",
        accessorFn: (row) => row?.name ?? "",
        cell: ({ row }) => {
            const cropZoneName = row.original.crop_zone_name;
            const cropZoneId = row.original.crop_zone_id;
            const cropZoneColor = row.original.crop_zone_color;
            return (
                <Link
                    to="/dashboard/fields/$fieldId"
                    params={{ fieldId: row.original.id.toString() }}
                    search={{
                        crop_zone:
                            cropZoneId != null ? cropZoneId : undefined,
                    }}
                    target="_blank"
                >
                    <div className="flex flex-col items-start justify-between w-full">
                        <div className="flex flex-col items-start min-w-0">
                            <span className="truncate">
                                {row.original.name}
                                {cropZoneName ? (
                                    <span
                                        className="text-xs font-medium ml-1.5"
                                        style={
                                            cropZoneColor
                                                ? { color: cropZoneColor }
                                                : undefined
                                        }
                                    >
                                        {cropZoneName}
                                    </span>
                                ) : null}
                            </span>
                        </div>
                        <div className="flex flex-row gap-1 justify-start">
                            <Chip
                                color={
                                    row.original.active_enrollment
                                        ? "primary"
                                        : "default"
                                }
                                radius="sm"
                                size="sm"
                                classNames={{
                                    base: "text-[0.6rem]",
                                    content: "p-[0.15rem]",
                                }}
                            >
                                {getEnrollmentTierLabel(
                                    row.original.active_enrollment
                                        ?.enrollment_tier,
                                )}
                            </Chip>
                            {!!row.original.active_enrollment && (
                                <InsightsActivityChip
                                    enrollmentTier={
                                        row.original.active_enrollment
                                            .enrollment_tier
                                    }
                                    insightsActive={
                                        row.original.active_enrollment
                                            .insights_active
                                    }
                                    waterInsightsActive={
                                        row.original.active_enrollment
                                            .water_insights_active
                                    }
                                />
                            )}
                        </div>
                    </div>
                </Link>
            );
        },
        size: 250,
    }),

    irrigation: <T extends BaseField>(): ColumnDef<T> => ({
        id: "irrigation",
        header: "Irrigation",
        enableSorting: false,
        accessorFn: (row) => row?.irrigation_system_configuration ?? "",
        cell: ({ row }) => {
            const irrig = row.original.irrigation_system_configuration;
            let status = "—";
            if (irrig) {
                status = irrig.count === 0 ? "Rainfed" : "Irrigated";
            }
            return (
                <Chip
                    radius="sm"
                    as={Link}
                    to="/dashboard/fields/$fieldId/setup/irrigation-system"
                    params={{
                        // @ts-expect-error ts(2322 )
                        fieldId: row.original.id.toString(),
                    }}
                    target="_blank"
                    className="text-xs"
                    variant="flat"
                    color={
                        status === "Irrigated"
                            ? "primary"
                            : status === "Rainfed"
                                ? "secondary"
                                : "default"
                    }
                    endContent={
                        <FontAwesomeIcon
                            icon={faUpRightFromSquare}
                            size="sm"
                            className="mr-2"
                        />
                    }
                >
                    {status}
                </Chip>
            );
        },
        size: 100,
    }),
};
